import './index.css';
import { MAX_HEIGHT, MAX_WIDTH } from '../../constant/thumb';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import LoadSpinner from '../../asset/load-spinner.svg';
import IconeUtilizacaoImagem from './IconeUtilizacaoImagem';
import { useSettingsStore } from '../../store/settings';
import { isViewMode, isFix } from '../../utils/helpers';
import ReactTooltip from 'react-tooltip';
import {
  startImageLoadWait,
  clearImageLoadWait,
  IMAGE_LOAD_ERROR_MSG
} from '../../utils/imageLoadTimeout';
const Thumb = ({
  source: sourceSrc,
  imgWidth,
  imgHeight,
  isManualSort,
  isPair,
  canDrop,
  isDragging,
  isOver,
  thumbId,
  deleteThumb,
  usedImage,
  imageId,
  toggleThumbExpand,
  updateThumbExpandIDB,
  thumbExpand,
  stackThumb,
  connectDragSource,
  connectDropTarget,
  prodType
}) => {
  const isAppEnabled = useSettingsStore(st => st.isAppEnabled);
  const [loadTimedOut, setLoadTimedOut] = useState(false);
  const loadTimeoutId = useRef(null);

  const source =
    isViewMode() || isFix() ? isAppEnabled && sourceSrc : sourceSrc;
  const rate = imgHeight / MAX_HEIGHT;
  const fImgWidth = imgWidth / rate;
  const fImgHeight = imgHeight / rate;
  const showLoadError = !source && loadTimedOut;
  const loadErrorTip = showLoadError ? IMAGE_LOAD_ERROR_MSG : null;

  const clearLoadTimer = () => {
    if (loadTimeoutId.current != null) {
      clearTimeout(loadTimeoutId.current);
      loadTimeoutId.current = null;
    }
  };

  useEffect(() => {
    clearLoadTimer();

    if (source) {
      clearImageLoadWait(imageId);
      setLoadTimedOut(false);
      return undefined;
    }

    const remaining = startImageLoadWait(imageId);
    if (remaining <= 0) {
      setLoadTimedOut(true);
      ReactTooltip.rebuild();
      return undefined;
    }

    setLoadTimedOut(false);
    loadTimeoutId.current = setTimeout(() => {
      setLoadTimedOut(true);
      ReactTooltip.rebuild();
    }, remaining);

    return clearLoadTimer;
  }, [source, imageId]);

  useEffect(
    () => () => {
      clearLoadTimer();
    },
    []
  );

  useEffect(() => {
    if (showLoadError) {
      requestAnimationFrame(() => ReactTooltip.rebuild());
    }
  }, [showLoadError, isAppEnabled]);

  return connectDropTarget(
    connectDragSource(
      <div
        className={`thumb${source ? '' : ' is-loading'}${
          showLoadError ? ' is-load-error' : ''
        }`}
        onMouseEnter={() =>
          thumbExpand.active !== 'CLICK' &&
          source &&
          !showLoadError &&
          toggleThumbExpand('HOVER', imageId)
        }
        onMouseLeave={() =>
          thumbExpand.active !== 'CLICK' && toggleThumbExpand()
        }
        onDragStart={() =>
          thumbExpand.active !== 'CLICK' && toggleThumbExpand()
        }
        onDoubleClick={() =>
          source &&
          !showLoadError &&
          toggleThumbExpand('CLICK', imageId) &&
          updateThumbExpandIDB()
        }
      >
        {stackThumb && stackThumb.blobUrl ? (
          <img
            src={stackThumb.blobUrl}
            alt={`Miniatura de foto ${stackThumb.id}`}
            className={`stack ${isPair ? 'pair' : 'odd'}`}
            width={`${stackThumb.width}px`}
            height={`${stackThumb.height}px`}
            style={{
              maxWidth: `${MAX_WIDTH}px`,
              maxHeight: `${MAX_HEIGHT}px`,
              left: `${(96 - stackThumb.width) / 2}px`
            }}
          />
        ) : null}
        <div
          className={`${source ? 'image-container' : 'loading-container'}${
            isPair ? ' pair' : ' odd'
          }${isManualSort ? ' manual-sort' : ''}${isOver ? ' isOver' : ''}${
            isDragging ? ' isDragging' : ''
          }${canDrop ? ' canDrop' : ''}${
            thumbExpand.id === imageId ? ' active' : ''
          }`}
        >
          <div className="white-layer"></div>
          {source ? (
            <img
              src={sourceSrc}
              alt={`Miniatura de foto ${thumbId}`}
              width={fImgWidth}
              height={fImgHeight}
              style={{
                maxWidth: `${MAX_WIDTH}px`,
                maxHeight: `${MAX_HEIGHT}px`
              }}
            />
          ) : showLoadError ? (
            <div
              className="loading load-error"
              style={{
                width: `${MAX_WIDTH}px`,
                height: `${MAX_HEIGHT}px`
              }}
            >
              <svg
                className="thumb-error-icon"
                viewBox="0 0 48 36"
                aria-hidden="true"
              >
                <circle cx="42" cy="6" r="6" fill="#999" />
                <path
                  d="M12 4 L28 24 L36 18 L48 36 L0 36 L12 4Z"
                  fill="#999"
                />
              </svg>
              {loadErrorTip && (
                <div
                  className="thumb-load-error-hitarea"
                  data-tip={loadErrorTip}
                  data-place="bottom"
                  data-class="type-warning"
                  data-effect="float"
                />
              )}
            </div>
          ) : (
            <div
              className="loading"
              style={{
                width: `${MAX_WIDTH}px`,
                height: `${MAX_HEIGHT}px`
              }}
            >
              <img
                src={LoadSpinner}
                alt=""
                className="thumb-loading-spinner"
              />
            </div>
          )}
          <IconeUtilizacaoImagem
            source={sourceSrc}
            prodType={prodType}
            usedImage={usedImage}
            fImgWidth={fImgWidth}
            fImgHeight={fImgHeight}
          />
          {source && (
            <div
              className="remove-btn bt-menu-delete"
              onClick={() => deleteThumb(imageId)}
            />
          )}
        </div>
      </div>
    )
  );
};

Thumb.propTypes = {
  source: PropTypes.string.isRequired,
  imgWidth: PropTypes.number.isRequired,
  imgHeight: PropTypes.number.isRequired,
  isManualSort: PropTypes.bool.isRequired,
  isPair: PropTypes.bool.isRequired,
  canDrop: PropTypes.bool.isRequired,
  isDragging: PropTypes.bool.isRequired,
  isOver: PropTypes.bool.isRequired,
  thumbId: PropTypes.string.isRequired,
  deleteThumb: PropTypes.func.isRequired,
  usedImage: PropTypes.number.isRequired,
  imageId: PropTypes.string.isRequired,
  toggleThumbExpand: PropTypes.func.isRequired,
  updateThumbExpandIDB: PropTypes.func.isRequired,
  thumbExpand: PropTypes.shape({
    active: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired
  }).isRequired,
  stackThumb: PropTypes.shape({
    blobUrl: PropTypes.string.isRequired,
    height: PropTypes.number.isRequired,
    id: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    width: PropTypes.number.isRequired
  }),
  connectDragSource: PropTypes.func.isRequired,
  connectDropTarget: PropTypes.func.isRequired,
  prodType: PropTypes.string.isRequired
};

export default Thumb;
